import {getSession} from 'next-auth/react'
import {deleteCookie} from 'cookies-next'
import type {NextRouter} from 'next/router'
import type {Dispatch, SetStateAction} from 'react'
// Utils
import {logDataByLevel} from './logging-helpers'
import {clearAllStoredVoterIds} from './voter-storage'
// Consts
import {PENDING_SIGNIN_TIMEOUT_MS} from '../consts/timing'
// Types
import type {CitysparkUser, AppSession, AuthenticationMismatchResult} from '../types/auth'
import type {Voter} from '../types/voter'

interface OIDCLoginParams {
  router: NextRouter
  ballotId: string
  citysparkId?: string | null
  previousUrlOverride?: string | null
  debugFromFunctionName?: string | null
}

interface SmartSignInParams {
  router: NextRouter
  ballotId: string
  citysparkId?: string | null
  voterId?: string | null
  forceCleanSignIn?: boolean
  previousUrlOverride?: string | null
}

interface UpstreamSignOutParams {
  router: NextRouter
  previousHrefOverride?: string | null
}

interface LocalSignOutCallbacks {
  setVoter?: Dispatch<SetStateAction<Voter | null>>
  sendVoterMenuIsIn?: (isIn: boolean) => void
}

interface HandleAuthFailureParams {
  error?: Error | null
  shouldForceLogout?: boolean
  ballotId: string
  citysparkId?: string | null
  router: NextRouter
}

export const citysparkOIDCLogin = ({
  router,
  ballotId,
  citysparkId = null,
  previousUrlOverride = null,
  debugFromFunctionName = null,
}: OIDCLoginParams): void => {
  let oauthRedirectUrl = new URL('auth/assign-cookies', process.env.NEXT_PUBLIC_EXTERNAL_AUTH_URL)
  const newParams: Record<string, string> = {
    citysparkId: citysparkId || '',
    previousBallotId: ballotId,
    previousUrl: previousUrlOverride || window.location.href,
  }
  const oauthParams = new URLSearchParams(newParams)
  logDataByLevel({
    level: 'info',
    message: 'citysparkOIDCLogin - redirect origin fetched',
    data: {
      ...newParams,
      environment: process.env.NEXT_PUBLIC_VERCEL_ENV,
      debugFromFunctionName,
    },
  })
  oauthRedirectUrl.search = oauthParams.toString()
  router.push(oauthRedirectUrl.toString())
}

export const checkCitysparkOauthStatus = async (): Promise<CitysparkUser | null> => {
  const session = (await getSession()) as AppSession | null
  return (session?.user as CitysparkUser) || null
}

export const checkAuthenticationMismatch = async (voterId: string | null): Promise<AuthenticationMismatchResult> => {
  try {
    const session = await getSession()
    const citysparkIsLoggedIn = Boolean(session?.user)
    const ballotIsLoggedIn = Boolean(voterId)

    return {
      citysparkIsLoggedIn,
      ballotIsLoggedIn,
      hasMismatch: citysparkIsLoggedIn && !ballotIsLoggedIn,
    }
  } catch (error) {
    logDataByLevel({
      level: 'error',
      message: 'Error checking authentication mismatch:',
      data: error,
    })
    return {
      citysparkIsLoggedIn: false,
      ballotIsLoggedIn: false,
      hasMismatch: false,
    }
  }
}

export const smartSignIn = async ({
  router,
  ballotId,
  citysparkId = null,
  voterId = null,
  forceCleanSignIn = false,
  previousUrlOverride = null,
}: SmartSignInParams): Promise<void> => {
  try {
    const authStatus = await checkAuthenticationMismatch(voterId)

    if (authStatus.hasMismatch || forceCleanSignIn) {
      const signInParams = {
        ballotId,
        citysparkId,
        timestamp: Date.now(),
      }
      localStorage.setItem('pendingSignIn', JSON.stringify(signInParams))

      await upstreamSignOut({
        router,
        previousHrefOverride: window.location.href,
      })
    } else {
      citysparkOIDCLogin({
        router,
        ballotId,
        citysparkId,
        previousUrlOverride,
        debugFromFunctionName: 'smartSignIn - no mismatch',
      })
    }
  } catch (error) {
    logDataByLevel({
      level: 'error',
      message: 'Error during smart sign-in:',
      data: error,
    })

    citysparkOIDCLogin({
      router,
      ballotId,
      citysparkId,
      previousUrlOverride,
      debugFromFunctionName: 'smartSignIn - error catch',
    })
  }
}

export const checkForPendingSignIn = (router: NextRouter): boolean => {
  try {
    const pendingSignIn = localStorage.getItem('pendingSignIn')
    if (pendingSignIn) {
      const params = JSON.parse(pendingSignIn)

      const isRecent = Date.now() - params.timestamp < PENDING_SIGNIN_TIMEOUT_MS

      if (isRecent) {
        localStorage.removeItem('pendingSignIn')
        // If retryAfterFailure is true. We retrying sign-in after authentication failure

        citysparkOIDCLogin({
          router,
          ballotId: params.ballotId,
          citysparkId: params.citysparkId,
          debugFromFunctionName: 'checkForPendingSignIn',
        })
      } else {
        // isRecent is false. Pending sign-in expired, removing from localStorage
        localStorage.removeItem('pendingSignIn')
      }
    }
  } catch (error) {
    logDataByLevel({
      level: 'error',
      message: 'Error checking for pending sign-in:',
      data: error,
    })

    localStorage.removeItem('pendingSignIn')
  }

  return false
}

export const upstreamSignOut = async ({
  router,
  previousHrefOverride = null,
}: UpstreamSignOutParams): Promise<boolean> => {
  try {
    // TODO: Implement CSRF token generation and use it in the request
    const session = (await getSession()) as AppSession | null

    let oidcSignOutUrl = new URL(`${process.env.NEXT_PUBLIC_EXTERNAL_AUTH_URL}/api/auth/cityspark-oidc-sign-out-callback`)
    if (session?.idToken) {
      oidcSignOutUrl.searchParams.set('idToken', session.idToken)
    }
    oidcSignOutUrl.searchParams.set('previousHref', previousHrefOverride || window.location.href)

    router.push(oidcSignOutUrl.toString())
    return true
  } catch {
    return false
  }
}

export const localSignOut = (callbacks?: LocalSignOutCallbacks): void => {
  deleteCookie('voter_id', {path: '/'})
  clearAllStoredVoterIds()
  callbacks?.setVoter?.(null)
  callbacks?.sendVoterMenuIsIn?.(false)
}

export const handleAuthenticationFailure = async ({
  error = null,
  shouldForceLogout = false,
  ballotId,
  citysparkId = null,
  router,
}: HandleAuthFailureParams): Promise<boolean> => {
  try {
    const session = await getSession()
    const citysparkIsAuthenticated = Boolean(session?.user)

    const failureDetails = {
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'server',
      timestamp: new Date().toISOString(),
      citysparkIsAuthenticated,

      error: error?.message || 'Unknown error',
      shouldForceLogout,
      ballotId,
      citysparkId,
    }

    logDataByLevel({
      level: 'error',
      message: 'Authentication failure detected',
      data: failureDetails,
    })

    if (citysparkIsAuthenticated || shouldForceLogout) {
      const signInParams = {
        ballotId,
        citysparkId,
        timestamp: Date.now(),
        retryAfterFailure: true,
        originalError: error?.message || 'Authentication failure',
      }
      localStorage.setItem('pendingSignIn', JSON.stringify(signInParams))

      await upstreamSignOut({
        router,
        previousHrefOverride: window.location.href,
      })

      return true
    }

    return false
  } catch (logoutError) {
    logDataByLevel({
      level: 'error',
      message: 'Error during authentication failure handling:',
      data: logoutError,
    })
    return false
  }
}
